import React, { useCallback, useState } from 'react';
import {
  Box, Button, Chip, Link, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ChangedFilesSection from './ChangedFilesSection';
import { requestError } from './messages';
import ReplacementsSection from './ReplacementsSection';
import type {
  ApiData, PropertyOption, SharePointBuilding, SharePointFile, SharePointMoveOut,
} from './types';

const API = 'api/integrations/sharepoint';

interface ReviewSectionProps {
  workspaceId: number;
  changedCount: number;
  moveOutCount: number;
  replacementCount: number;
}

interface BuildingRowProps {
  building: SharePointBuilding;
  properties: PropertyOption[];
  onConfirm: (key: string, propertyId: number) => void;
}

function suggestionText(building: SharePointBuilding): string {
  if (building.property_id === null) {
    return 'No linked leases yet';
  }
  const percent = Math.round(building.share * 100);
  return `${building.property_name} (${percent}% of ${building.total} leases)`;
}

function BuildingRow({ building, properties, onConfirm }: BuildingRowProps): JSX.Element {
  // Pre-fill only when every lease in the folder agrees; a split folder's top property is often the wrong one.
  const [choice, setChoice] = useState<number | ''>(building.basis === 'unanimous' ? building.property_id ?? '' : '');
  return (
    <TableRow>
      <TableCell>{building.key}</TableCell>
      <TableCell>{suggestionText(building)}</TableCell>
      <TableCell>
        <Select
          size='small'
          displayEmpty
          value={choice}
          sx={{ minWidth: 220 }}
          onChange={(e: SelectChangeEvent<number | ''>): void => { setChoice(e.target.value === '' ? '' : Number(e.target.value)); }}
        >
          <MenuItem value=''><em>Choose a property</em></MenuItem>
          {properties.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell align='right'>
        <Button size='small' variant='outlined' disabled={choice === ''} onClick={(): void => { if (choice !== '') { onConfirm(building.key, choice); } }}>
          Confirm
        </Button>
      </TableCell>
    </TableRow>
  );
}

interface MoveOutAction {
  moveOut: SharePointMoveOut;
  action: 'still_here' | 'stop_tracking';
}

function moveOutReason(moveOut: SharePointMoveOut): string {
  return moveOut.reason === 'moved_to_former_tenants'
    ? 'Lease files moved to a Former Tenants folder'
    : 'Lease files removed from SharePoint';
}

export default function ReviewSection({
  workspaceId, changedCount, moveOutCount, replacementCount,
}: ReviewSectionProps): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: files = [] } = useQuery(
    ['get/sharepoint-items', workspaceId, 'unmatched'],
    async () => axios({ url: `${API}/items/`, method: 'get', params: { workspace: workspaceId, status: 'unmatched' } }),
    { select: (res: AxiosResponse<ApiData<SharePointFile[]>>) => res.data.data },
  );
  const { data: moveOuts = [] } = useQuery(
    ['get/sharepoint-move-outs', workspaceId],
    async () => axios({ url: `${API}/move-outs/`, method: 'get', params: { workspace: workspaceId } }),
    { enabled: moveOutCount > 0, select: (res: AxiosResponse<ApiData<SharePointMoveOut[]>>) => res.data.data },
  );
  const { data: buildings = [] } = useQuery(
    ['get/sharepoint-buildings', workspaceId],
    async () => axios({ url: `${API}/buildings/`, method: 'get', params: { workspace: workspaceId } }),
    { select: (res: AxiosResponse<ApiData<SharePointBuilding[]>>) => res.data.data.filter((b) => !b.auto && !b.confirmed) },
  );
  const { data: properties = [] } = useQuery(
    ['get/sharepoint-property-options', workspaceId],
    async () => axios({ url: `api/filter/property/?workspace=${workspaceId}`, method: 'get' }),
    {
      select: (res: AxiosResponse<{ detail?: { id: number; address: string; name: string }[] }>): PropertyOption[] => (res.data.detail ?? []).map((p) => ({ id: p.id, label: p.name !== '' ? p.name : p.address }))
      ,
    },
  );

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries('get/sharepoint-items'),
      queryClient.invalidateQueries('get/sharepoint-buildings'),
      queryClient.invalidateQueries('get/sharepoint-status'),
      queryClient.invalidateQueries('get/sharepoint-move-outs'),
      queryClient.invalidateQueries('get/leases'),
      queryClient.invalidateQueries('get/options-monitor'),
    ]);
  }, [queryClient]);

  const { mutate: processFile, isLoading: processing } = useMutation(
    async (file: SharePointFile) => axios({ url: `${API}/items/${file.id}/process/`, method: 'post', params: { workspace: workspaceId } }),
    {
      onSuccess: async (_res: AxiosResponse, file: SharePointFile): Promise<void> => {
        enqueueSnackbar(`Sent "${file.name}" for processing. It will appear on the Leases page shortly.`);
        await refresh();
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'process the file'), { variant: 'error' }); },
    },
  );
  const { mutate: actOnMoveOut, isLoading: actingOnMoveOut } = useMutation(
    async ({ moveOut, action }: MoveOutAction) => axios({
      url: `${API}/move-outs/${moveOut.lease_id}/`, method: 'post', params: { workspace: workspaceId }, data: { action },
    }),
    {
      onSuccess: async (_res: AxiosResponse, { moveOut, action }: MoveOutAction): Promise<void> => {
        enqueueSnackbar(action === 'stop_tracking'
          ? `Stopped tracking ${moveOut.tenant_name}'s options. They're marked not applicable in the Options Monitor.`
          : `Kept ${moveOut.tenant_name}'s options.`);
        await refresh();
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'update the lease'), { variant: 'error' }); },
    },
  );
  const { mutate: confirmBuilding } = useMutation(
    async ({ key, propertyId }: { key: string; propertyId: number }) => axios({
      url: `${API}/buildings/`, method: 'post', params: { workspace: workspaceId }, data: { key, property_id: propertyId },
    }),
    {
      onSuccess: async (): Promise<void> => {
        enqueueSnackbar('Building folder confirmed. New leases in it will use that property.');
        await refresh();
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'confirm the building folder'), { variant: 'error' }); },
    },
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
        {`Files to review (${files.length})`}
      </Typography>
      {files.length === 0 ? (
        <Typography color='text.secondary' sx={{ mb: 2 }}>Every lease file in SharePoint is linked to a lease in Strada.</Typography>
      ) : (
        <Table size='small' sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Found</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id}>
                <TableCell>
                  <Link href={file.web_url} target='_blank' rel='noopener noreferrer'>{file.name}</Link>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>{file.path}</Typography>
                </TableCell>
                <TableCell>{file.lease_tenant_name ?? 'New tenant folder'}</TableCell>
                <TableCell>
                  <Chip
                    size='small'
                    color={file.found_at_connect ? 'default' : 'info'}
                    label={file.found_at_connect ? 'Already there when connected' : 'New, imports automatically'}
                  />
                </TableCell>
                <TableCell align='right'>
                  <Button size='small' variant='outlined' disabled={processing} onClick={(): void => { processFile(file); }}>
                    Process
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ChangedFilesSection workspaceId={workspaceId} changedCount={changedCount} onUpdated={refresh} />
      <ReplacementsSection workspaceId={workspaceId} replacementCount={replacementCount} onUpdated={refresh} />

      {moveOuts.length > 0 && (
        <>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            {`Tenant may have left (${moveOuts.length})`}
          </Typography>
          <Typography color='text.secondary' sx={{ mb: 1 }}>
            Strada still tracks these leases&apos; options, but their files left the tenant&apos;s Lease folder in SharePoint. Strada never ends a lease on its own.
          </Typography>
          <Table size='small' sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>What changed</TableCell>
                <TableCell>Options tracked</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {moveOuts.map((moveOut) => (
                <TableRow key={moveOut.lease_id}>
                  <TableCell>
                    {moveOut.tenant_name}
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>{moveOut.property_name}</Typography>
                  </TableCell>
                  <TableCell>
                    {moveOutReason(moveOut)}
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>{moveOut.example_path}</Typography>
                  </TableCell>
                  <TableCell>{moveOut.active_options}</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <Button size='small' disabled={actingOnMoveOut} sx={{ mr: 1 }} onClick={(): void => { actOnMoveOut({ moveOut, action: 'still_here' }); }}>
                      Still a tenant
                    </Button>
                    <Button size='small' variant='outlined' disabled={actingOnMoveOut} onClick={(): void => { actOnMoveOut({ moveOut, action: 'stop_tracking' }); }}>
                      Stop tracking options
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
        {`Building folders to confirm (${buildings.length})`}
      </Typography>
      {buildings.length === 0 ? (
        <Typography color='text.secondary'>Every building folder is matched to a property.</Typography>
      ) : (
        <>
          <Typography color='text.secondary' sx={{ mb: 1 }}>
            New leases in these folders get no property until you confirm one.
          </Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Folder</TableCell>
                <TableCell>Leases in it are on</TableCell>
                <TableCell>Property</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {buildings.map((building) => (
                <BuildingRow
                  key={building.key}
                  building={building}
                  properties={properties}
                  onConfirm={(key: string, propertyId: number): void => { confirmBuilding({ key, propertyId }); }}
                />
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}
